import React, { useRef, useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { appLocalStore, ConsoleLogType } from '../stores/appLocalStore';

const ConsolePanel: React.FC = observer(() => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const consoleStore = appLocalStore.debug.console;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current && consoleStore.visible) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [consoleStore.messages.length, consoleStore.visible]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const windowHeight = window.innerHeight;
    const mouseY = e.clientY;
    const newHeight = ((windowHeight - mouseY) / windowHeight) * 100;
    
    consoleStore.setHeightAction(newHeight);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleClear = useCallback(() => {
    consoleStore.clearMessagesAction();
  }, []);

  const getEmojiForType = useCallback((type: ConsoleLogType): string => {
    switch (type) {
      case 'log': return '📝';
      case 'info': return 'ℹ️';
      case 'warn': return '⚠️';
      case 'error': return '❌';
      case 'debug': return '🐛';
      default: return '📝';
    }
  }, []);

  const getColorForType = useCallback((type: ConsoleLogType): string => {
    switch (type) {
      case 'log': return 'text-[var(--text-primary)]';
      case 'info': return 'text-blue-400';
      case 'warn': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      case 'debug': return 'text-purple-400';
      default: return 'text-[var(--text-primary)]';
    }
  }, []);

  const formatArgs = useCallback((args: any[]): string => {
    return args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
  }, []);

  const formatTimestamp = useCallback((date: Date): string => {
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${timeStr}.${ms}`;
  }, []);

  if (!appLocalStore.debug.console.visible) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="fixed bottom-0 left-0 right-0 bg-[var(--bg-primary)] border-t-2 border-indigo-600 shadow-2xl z-[9998] flex flex-col"
      style={{ height: `${consoleStore.panelHeight}vh` }}
    >
      {/* Grabber for resizing */}
      <div
        className="h-2 bg-indigo-600 cursor-ns-resize hover:bg-indigo-700 transition-colors flex items-center justify-center"
        onMouseDown={handleMouseDown}
      >
        <div className="w-16 h-1 bg-white/50 rounded-full"></div>
      </div>

      {/* Header */}
      <div className="bg-indigo-600 text-white px-4 py-2 flex justify-between items-center">
        <h3 className="m-0 text-base font-semibold">Console Panel</h3>
        <div className="flex items-center gap-3">
          <span className="bg-white/20 px-2 py-1 rounded text-xs font-medium">
            {consoleStore.messages.length} message(s)
          </span>
          <button
            onClick={handleClear}
            className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs font-medium transition-colors"
            title="Clear console"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Console messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-2 font-mono text-xs">
        {consoleStore.messages.length === 0 ? (
          <div className="text-center text-[var(--text-secondary)] py-8 italic">
            No console messages yet
          </div>
        ) : (
          consoleStore.messages.map((message) => (
            <div
              key={message.id}
              className="flex gap-2 px-2 py-1 hover:bg-[var(--bg-secondary)] border-b border-[var(--border-color)] last:border-b-0"
            >
              <span className="flex-shrink-0">{getEmojiForType(message.type)}</span>
              <span className="flex-shrink-0 text-[var(--text-secondary)] w-24">
                {formatTimestamp(message.timestamp)}
              </span>
              <span className={`flex-shrink-0 uppercase font-semibold w-14 ${getColorForType(message.type)}`}>
                {message.type}
              </span>
              <span className={`flex-1 break-words whitespace-pre-wrap ${getColorForType(message.type)}`}>
                {formatArgs(message.args)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
});

export default ConsolePanel;
